import axios, { AxiosRequestConfig } from 'axios';

import { SYNTIRO_AGENT_API_ENDPOINT } from '../../../env';
import { getAuthHeaderForUser, handleAuthError } from '../../../lib/auth/auth.util';
import type { AskRequest, AskResponse, ProblemDetail } from '../types/syntiro-agent';

const FALLBACK_ERROR_DETAIL =
  'Syntiro could not be reached or returned an unexpected error. Please try again.';

const ASK_TIMEOUT_MS = 90_000;

export class SyntiroAgentApiError extends Error {
  public readonly problem: ProblemDetail;

  constructor(problem: ProblemDetail) {
    super(problem.detail ?? problem.title ?? FALLBACK_ERROR_DETAIL);
    Object.setPrototypeOf(this, SyntiroAgentApiError.prototype);
    this.name = 'SyntiroAgentApiError';
    this.problem = problem;
  }
}

function isProblemDetail(body: unknown): body is ProblemDetail {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) {
    return false;
  }
  const candidate = body as Record<string, unknown>;
  return typeof candidate.title === 'string' || typeof candidate.detail === 'string';
}

/**
 * Normalizes any thrown value into a `ProblemDetail`. Branch order is
 * load-bearing: a non-`HttpException` 500 puts
 * `{"title":"Internal Server Error","status":500}` on the wire, which passes
 * the `isProblemDetail` guard below — so the 5xx branch must run before the
 * passthrough branch, or the raw title renders into the chat. A timeout
 * surfaces as `ECONNABORTED`/`ETIMEDOUT` with no `error.response`, so it needs
 * its own branch ahead of the generic "no response" case, or the user is told
 * the service is unreachable while it is still thinking.
 */
export function toProblemDetail(error: unknown): ProblemDetail {
  if (error instanceof SyntiroAgentApiError) {
    return error.problem;
  }
  if (axios.isAxiosError(error)) {
    if (error.response && error.response.status >= 500) {
      return {
        type: 'about:blank',
        title: 'Agent error',
        status: error.response.status,
        detail: 'Syntiro hit an error answering that. Please try again.'
      };
    }
    const body: unknown = error.response?.data;
    if (isProblemDetail(body)) {
      return body;
    }
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return {
        type: 'about:blank',
        title: 'Request timed out',
        detail: 'Syntiro took too long to answer. Please try again.'
      };
    }
    return {
      type: 'about:blank',
      title: error.response ? 'Request failed' : 'Service unreachable',
      status: error.response?.status,
      detail: error.message || FALLBACK_ERROR_DETAIL
    };
  }
  return {
    type: 'about:blank',
    title: 'Unexpected error',
    detail: error instanceof Error && error.message ? error.message : FALLBACK_ERROR_DETAIL
  };
}

async function askRequest<ResponseBody>(config: AxiosRequestConfig): Promise<ResponseBody> {
  try {
    const authHeader = await getAuthHeaderForUser();
    const response = await axios.request<ResponseBody>({
      baseURL: SYNTIRO_AGENT_API_ENDPOINT,
      timeout: ASK_TIMEOUT_MS,
      ...config,
      headers: { ...authHeader, ...config.headers }
    });
    return response.data;
  } catch (err: unknown) {
    // Caller-initiated aborts are control flow, not failures — rethrow before
    // the wrap, because SyntiroAgentApiError is opaque to axios.isCancel
    // downstream in the hook.
    if (axios.isCancel(err)) throw err;
    handleAuthError(err);
    throw new SyntiroAgentApiError(toProblemDetail(err));
  }
}

export async function askSyntiro(input: AskRequest, signal?: AbortSignal): Promise<AskResponse> {
  return askRequest<AskResponse>({ method: 'post', url: '/ask', data: input, signal });
}
